import React from 'react';
import { Box, Card, CardActionArea, Stack, Typography, alpha, useTheme, Skeleton } from '@mui/material';
import {
  EmojiEvents, Info as InfoIcon, Warning as WarningIcon, Receipt, Block,
  LabelOutlined,
} from '@mui/icons-material';
import { LetterCategory } from '../../types/letters';
import { severityTone } from '../../utils/severityTone';

const ICON_MAP: Record<string, React.ReactNode> = {
  EmojiEvents: <EmojiEvents />,
  Info: <InfoIcon />,
  Warning: <WarningIcon />,
  Receipt: <Receipt />,
  Block: <Block />,
};
const renderIcon = (name?: string) => ICON_MAP[name || ''] || <LabelOutlined />;

interface Props {
  categories: LetterCategory[];
  loading?: boolean;
  selectedId?: string;
  onSelect: (cat: LetterCategory) => void;
}

/**
 * Card grid for picking a letter category. Each card shows the category icon,
 * label, and a one-line hint about required fields. Severity drives the tone
 * so the picker visually communicates the seriousness of each action.
 */
const LetterCategoryPicker: React.FC<Props> = ({ categories, loading, selectedId, onSelect }) => {
  const theme = useTheme();

  if (loading) {
    return (
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(3, 1fr)' }, gap: 1.5 }}>
        {[0, 1, 2, 3, 4].map(i => (
          <Skeleton key={i} variant="rounded" height={108} />
        ))}
      </Box>
    );
  }

  if (categories.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 5, color: 'text.secondary' }}>
        <LabelOutlined sx={{ fontSize: 36, mb: 1, opacity: 0.6 }} />
        <Typography variant="body2">No categories configured.</Typography>
        <Typography variant="caption">Settings → Letter Categories to add one.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(3, 1fr)' }, gap: 1.5 }}>
      {categories.map(cat => {
        const tone = severityTone(theme, cat.severity);
        const isSelected = selectedId === cat._id;
        const hint = cat.requiresAmount
          ? cat.requiresReason ? 'Amount + reason' : 'Amount required'
          : cat.requiresReason ? 'Reason required' : 'Title only';
        return (
          <Card
            key={cat._id}
            elevation={0}
            sx={{
              borderRadius: 2,
              border: '1.5px solid',
              borderColor: isSelected ? tone.main : theme.palette.divider,
              bgcolor: isSelected ? alpha(tone.main, 0.06) : 'background.paper',
              transition: 'all 0.15s ease',
              '&:hover': { borderColor: tone.main, bgcolor: alpha(tone.main, 0.04) },
            }}
          >
            <CardActionArea onClick={() => onSelect(cat)} sx={{ p: 1.5, height: '100%' }}>
              <Stack direction="row" spacing={1.5} alignItems="flex-start">
                <Box sx={{
                  width: 36, height: 36, borderRadius: 1.5, flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  bgcolor: alpha(tone.main, 0.12), color: tone.main,
                }}>
                  {renderIcon(cat.icon)}
                </Box>
                <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.3 }}>
                    {cat.label}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.3 }}>
                    {hint}
                  </Typography>
                </Stack>
              </Stack>
            </CardActionArea>
          </Card>
        );
      })}
    </Box>
  );
};

export default LetterCategoryPicker;
